import type { EventData } from "@/lib/events/types"

/** Normalized address from geocoding / persistence (single source of truth for display). */
export type Address = {
  formatted: string
  line1?: string
  line2?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  placeId?: string
  lat?: number
  lng?: number
}

export type EventAddressFields = Pick<
  EventData,
  "location" | "locationName" | "address" | "lat" | "lng"
>

function trimOrEmpty(s: string | undefined | null): string {
  return s?.trim() ?? ""
}

/** Build {@link Address} from persisted event fields. */
export function eventFieldsToAddress(e: EventAddressFields): Address {
  const formatted =
    trimOrEmpty(e.address) ||
    trimOrEmpty(e.locationName) ||
    trimOrEmpty(e.location)
  return {
    formatted,
    lat: e.lat,
    lng: e.lng,
  }
}

function fallbackFormatted(a: Address): string {
  const f = trimOrEmpty(a.formatted)
  if (f) return f
  const parts = [
    trimOrEmpty(a.line1),
    trimOrEmpty(a.line2),
    trimOrEmpty(a.city),
    trimOrEmpty(a.state),
    trimOrEmpty(a.postalCode),
    trimOrEmpty(a.country),
  ].filter(Boolean)
  return parts.join(", ")
}

function displayCore(
  address: Address | EventAddressFields,
): { formatted: string } {
  const a =
    "location" in address ? eventFieldsToAddress(address) : { ...address }
  const formatted = trimOrEmpty(a.formatted) || fallbackFormatted(a)
  return { formatted }
}

/**
 * Enterprise display strings: prefer `formatted`; never synthesize city/state
 * when `formatted` is present (only split `formatted` for two-line layout).
 */
export function getDisplayAddress(
  address: Address | EventAddressFields,
  variant: "single",
): string
export function getDisplayAddress(
  address: Address | EventAddressFields,
  variant: "multi",
): { line1: string; line2: string }
export function getDisplayAddress(
  address: Address | EventAddressFields,
  variant: "single" | "multi",
): string | { line1: string; line2: string } {
  const { formatted } = displayCore(address)

  if (variant === "single") {
    return formatted
  }

  if (!formatted) {
    return { line1: "", line2: "" }
  }

  const commaIdx = formatted.indexOf(",")
  if (commaIdx === -1) {
    return { line1: formatted, line2: "" }
  }

  const line1 = formatted.slice(0, commaIdx).trim()
  const line2 = formatted.slice(commaIdx + 1).trim()
  return {
    line1: line1 || formatted,
    line2: line1 ? line2 : "",
  }
}
